import {
    collection,
    doc,
    getDoc,
    setDoc,
    updateDoc,
    query,
    where,
    getDocs
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { logger } from "@/lib/logger";

export interface SubscriptionPlan {
    id: string;
    name: string;
    price: number;
    interval: 'month' | 'year';
    features: string[];
    maxUsers: number;
    active: boolean;
}

export interface TenantSubscription {
    tenantId: string;
    planId: string;
    status: 'active' | 'canceled' | 'past_due' | 'trialing';
    currentPeriodStart: Date;
    currentPeriodEnd: Date;
    cancelAtPeriodEnd: boolean;
    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
}

const PLANS_COLLECTION = "subscription_plans";
const SUBSCRIPTIONS_COLLECTION = "subscriptions";

export const DEFAULT_PLANS: SubscriptionPlan[] = [
    {
        id: 'starter',
        name: 'Starter',
        price: 29,
        interval: 'month',
        features: ['Bis zu 3 Nutzer', 'Basis Lead-Management', 'E-Mail Support'],
        maxUsers: 3,
        active: true
    },
    {
        id: 'pro',
        name: 'Pro',
        price: 79,
        interval: 'month',
        features: ['Bis zu 10 Nutzer', 'Erweitertes Reporting', 'Priorisierter Support', 'API Zugriff'],
        maxUsers: 10,
        active: true
    },
    {
        id: 'enterprise',
        name: 'Enterprise',
        price: 199,
        interval: 'month',
        features: ['Unbegrenzte Nutzer', 'Custom Features', '24/7 Support', 'Dedicated Account Manager'],
        maxUsers: 9999,
        active: true
    }
];

/**
 * Initialize default plans if they don't exist
 */
export async function initializePlans() {
    try {
        const plansRef = collection(db, PLANS_COLLECTION);
        const snapshot = await getDocs(plansRef);

        if (snapshot.empty) {
            for (const plan of DEFAULT_PLANS) {
                await setDoc(doc(db, PLANS_COLLECTION, plan.id), plan);
            }
            logger.log("Initialized default subscription plans");
        }
    } catch (error) {
        logger.error("Error initializing plans:", error);
    }
}

/**
 * Get all active subscription plans
 */
export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
        // For now, just return default plans to avoid extra reads if not needed, 
        // or fetch from DB if dynamic. Let's fetch from DB or fallback.
        const plansRef = collection(db, PLANS_COLLECTION);
        const q = query(plansRef, where("active", "==", true));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return DEFAULT_PLANS;
        }

        return snapshot.docs.map(doc => doc.data() as SubscriptionPlan);
    } catch (error) {
        logger.error("Error fetching plans:", error);
        return DEFAULT_PLANS;
    }
}

/**
 * Get subscription for a tenant
 */
export async function getTenantSubscription(tenantId: string): Promise<TenantSubscription | null> {
    try {
        const docRef = doc(db, SUBSCRIPTIONS_COLLECTION, tenantId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            return {
                ...data,
                currentPeriodStart: data.currentPeriodStart?.toDate(),
                currentPeriodEnd: data.currentPeriodEnd?.toDate(),
            } as TenantSubscription;
        }

        return null;
    } catch (error) {
        logger.error("Error fetching tenant subscription:", error);
        return null;
    }
}

/**
 * Create or update subscription
 */
export async function updateSubscription(tenantId: string, data: Partial<TenantSubscription>) {
    try {
        const docRef = doc(db, SUBSCRIPTIONS_COLLECTION, tenantId);
        await setDoc(docRef, data, { merge: true });
        logger.log("Updated subscription for tenant:", tenantId);
    } catch (error) {
        logger.error("Error updating subscription:", error);
        throw error;
    }
}
